import { useState, useEffect } from 'react';

// Function to parse content - only handles <text> as bold titles on separate lines
const parseContent = (content) => {
  if (!content) return content;

  // Split content by <...> patterns
  const parts = content.split(/(<[^>]+>)/g);

  return parts.map((part, index) => {
    // Check if this part is wrapped in <>
    if (part.startsWith('<') && part.endsWith('>')) {
      // Remove the <> and make it a bold title on its own line
      const titleText = part.slice(1, -1);
      return (
        <h4 key={index} className="font-bold text-gray-900 text-base mb-2 mt-4 first:mt-0">
          {titleText}
        </h4>
      );
    } else if (part.trim()) {
      // Regular text
      return (
        <p key={index} className="text-gray-600 text-sm leading-relaxed mb-2 whitespace-pre-wrap">
          {part}
        </p>
      );
    }
    return null;
  }).filter(Boolean);
};

export default function ProductHub() {
  const [activeTab, setActiveTab] = useState('updates');
  const [updates, setUpdates] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProductUpdates();
  }, []);

  const fetchProductUpdates = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.insightos.co.uk/api/productupdates', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Separate blogs and updates based on Blog field
      const blogsData = data.data.filter(item => item.Blog === 1);
      const updatesData = data.data.filter(item => item.Blog === 0);

      setBlogs(blogsData);
      setUpdates(updatesData);
      setError(null);
    } catch (error) {
      console.error('Error fetching product updates:', error);
      setError('Failed to load content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (items) => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchProductUpdates}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
            <p className="text-gray-600">
              {activeTab === 'blogs' ? 'No blogs available at the moment.' : 'No updates available at the moment.'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-200 w-full"
          >
            {item.ImageUrl && (
              <div className="w-full overflow-hidden">
                <img
                  src={item.ImageUrl}
                  alt={item.Title}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            <div className="p-6">
              <h3 className="text-xl font-bold text-indigo-600 mb-3">
                {item.Title}
              </h3>
              <div className="text-gray-600 text-sm leading-relaxed w-full break-words">
                {parseContent(item.Content)}
              </div>
              <div className="mt-4 flex items-center text-indigo-600 text-sm">
                {item.Author && (
                  <>
                    <span>By {item.Author}</span>
                    {item.CreatedAt && <span className="mx-2 text-gray-400">|</span>}
                  </>
                )}
                {item.CreatedAt && (
                  <span>{new Date(item.CreatedAt).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 font-bebas sm:text-2xl sm:tracking-tight">
            Product Hub
            </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Stay updated with the latest app features and read our insightful blogs
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl shadow-lg p-1 border border-gray-200">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('updates')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'updates'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                App Updates
              </button>
              <button
                onClick={() => setActiveTab('blogs')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === 'blogs'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Blogs
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[400px]">
          {activeTab === 'updates' ? renderContent(updates) : renderContent(blogs)}
        </div>
      </div>
    </div>
  );
}
